// src/services/api.ts
import {
    CashDrawer,
    CashDrawerConfiguration,
    ListTransactionTypesResponse,
    NonSaleTransactionRequest,
    CashTransactionResponse,
    CashSaleCompletionRequest,
    ActivateCashDrawerRequest,
    CashCountRequest,
    CountResponse,
    CashCountResponse,
    CheckPayment,
    PerformCashCountRequest,
    SubmitCountVarianceRequest,
    SubmitCountVarianceResponse,
    DailyReportsResponseSchema,
    WitnessSubmitRequest,
    DeactivateCashDrawerRequest,
    UpdateDrawerRequest
} from '../api/types'
import newStoreClient from './clients/newstore-proxy.client'
import awsBackendClient from './clients/aws-backend.client'
import { getNewStoreData } from 'services/util'

export function currentDrawerId(): string {
    const { storeId } = getNewStoreData()
    if (!storeId) throw new Error('Missing storeId from NewStore context')
    return storeId;
}

/**
 * Fetch a cash drawer’s details.
 * GET /_/v0/cash_drawers/{drawer_id}
 */
export async function getCashDrawer(): Promise<CashDrawer> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.get<CashDrawer>(`/_/v0/cash_drawers/${id}`);
    return data;
}

/**
 * Perform a cash count to get count_id.
 * POST /_/v0/cash_drawers/{drawer_id}/_count
 */
export async function performCashCount(request: PerformCashCountRequest): Promise<CountResponse> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.post<CountResponse>(`/_/v0/cash_drawers/${id}/_count`, request);
    return data;
}

/**
 * Record the detailed counts for a cash count.
 * POST /_/v0/cash_drawers/{drawer_id}/counts/{count_id}
 */
export async function recordCashCount(
    countId: string,
    request: CashCountRequest
): Promise<CashCountResponse> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.patch<CashCountResponse>(`/_/v0/cash_drawers/${id}/counts/${countId}`, request);
    return data;
}

/**
 * Initiates a recount, submits the final count, or cancels the cash count.
 * GET /_/v0/cash_drawers/{drawer_id}/counts/{count_id}/{action}
 */
export async function setCashCount(
    countId: string,
    action: string
): Promise<CashDrawer> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.post<CashDrawer>(
        `/_/v0/cash_drawers/${id}/counts/${countId}/${action}`
    );
    return data;
}

/**
 * Fetch the store’s cash‐drawer configuration flags.
 * GET /cash_drawers/configuration
 */
export async function getCashDrawerConfiguration(): Promise<CashDrawerConfiguration> {
    const { data } = await newStoreClient.get<CashDrawerConfiguration>(
        `/cash_drawers/configuration`
    );
    return data;
}

/**
 * List all supported transaction types & their config.
 * GET /cash_drawers/transaction_types
 */
export async function listTransactionTypes(): Promise<ListTransactionTypesResponse> {
    const { data } = await newStoreClient.get<ListTransactionTypesResponse>(
        `/_/v0/cash_drawer_config/transaction_types`
    );
    return data;
}

/**
 * Start a non‐sale transaction on a drawer.
 * POST /cash_drawers/{drawer_id}/transactions/non_sale
 */
export async function startNonSaleTransaction(
    request: NonSaleTransactionRequest
): Promise<CashTransactionResponse> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.post<CashTransactionResponse>(
        `/_/v0/cash_drawers/${id}/_non_sale`,
        request
    );
    return data;
}

/**
 * Complete or cancel a cash sale/exchange.
 * POST /cash_transactions/{transaction_id}/complete
 */
export async function finishCashSaleTransaction(
    transactionId: string,
    request: CashSaleCompletionRequest
): Promise<CashTransactionResponse> {
    const id = currentDrawerId();
    const { data } = await newStoreClient.post<CashTransactionResponse>(`/_/v0/cash_drawers/${id}/transactions/${transactionId}`, request);
    return data;
}

/**
 * Get daily reports
 * GET /_/v0/cash_drawers/${id}/daily_reports
 */
export async function getDailyReports(
): Promise<DailyReportsResponseSchema> {
    const id = currentDrawerId();
    const { data } = await newStoreClient.get<DailyReportsResponseSchema>(`/_/v0/cash_drawers/${id}/daily_reports`);
    return data;
}

/**
 * Activate a cash drawer by inserting float.
 * POST /cash_drawers/{drawer_id}/activate
 */
export async function activateCashDrawer(
    request: ActivateCashDrawerRequest
): Promise<CashTransactionResponse> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.post<CashTransactionResponse>(
        `/_/v0/cash_drawers/${id}/_activate`,
        request
    );
    return data;
}

/**
 * Disables the specified cash drawer for daily usage.
 * POST /_/v0/cash_drawers/{drawer_id}/_deactivate
 */
export async function deactivateCashDrawer(
    request: DeactivateCashDrawerRequest
): Promise<string> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.post<string>(
        `/_/v0/cash_drawers/${id}/_deactivate`,
        request
    );
    return data;
}


/**
 * Update the cash drawer configuration.
 * POST /_/v0/cash_drawers/{drawer_id}/counts/{count_id}
 */
export async function updateCashDrawer(
    request: UpdateDrawerRequest
): Promise<string> {
    const id = currentDrawerId()
    const { data } = await newStoreClient.patch<string>(`/_/v0/cash_drawers/${id}`, request);
    return data;
}

/**
 * Get optimal float amount.
 * GET /float/optimal/{drawer_id}
 */
export async function getOptimalFloat(): Promise<number> {
    const id = currentDrawerId()
    const { data } = await awsBackendClient.get<{ optimalFloat: number }>(
        `/float/optimal/${id}`
    )
    return data.optimalFloat;
}

/**
 * Fetch all cheque payments recorded today.
 * GET /cheque/record
 */
export async function getCheckPayments(storeIdDate: string): Promise<CheckPayment[]> {
    const { data } = await awsBackendClient.get<CheckPayment[]>(`/check/record/${storeIdDate}`)
    return data;
}

/**
 * Persist count variance (with reason) to DynamoDB.
 * POST /count‐variance
 */
export async function submitCountVariance(
    req: SubmitCountVarianceRequest
): Promise<SubmitCountVarianceResponse> {
    const { data } = await awsBackendClient.post<SubmitCountVarianceResponse>(`/count/submit-variance`, req);
    return data;
}

/**
 * POST /witness
 */
export async function submitWitness(request: WitnessSubmitRequest) {
    const { data } = await awsBackendClient.post(`/witness`, request);
    return data;
}

/**
 * Verify that the current user is authorized to access cash management.
 * GET /cash-management/auth/check
 */
export async function checkCashManagementAuth(): Promise<void> {
    await awsBackendClient.get(`/cash-management/auth/check`);
}

/**
 * Upsert the current cash drawer store status in DynamoDB.
 * PUT /cash-drawer/stores/{storeId}/status
 */
export async function upsertCashDrawerStoreStatus(params: {
    activationTimestamp?: string | null;
    deactivationTimestamp?: string | null;
}): Promise<void> {
    const storeId = currentDrawerId();
    const body: {
        activationTimestamp?: string | null;
        deactivationTimestamp?: string | null;
    } = {};

    if (params.activationTimestamp !== undefined) {
        body.activationTimestamp = params.activationTimestamp;
    }
    if (params.deactivationTimestamp !== undefined) {
        body.deactivationTimestamp = params.deactivationTimestamp;
    }
    await awsBackendClient.put(
        `/cash-drawer/stores/${storeId}/status`,
        body
    );
}
